import classNames from 'classnames';
import { Editor, useEditorState } from '@tiptap/react';

import { Icon } from '@/components/icon';
import { Tooltip } from '@/components/tooltip';
import { ShortcutIndicator } from '@/components/shortcut-indicator';

import { BlockType, BlockTypeDropdown } from './block-type-dropdown';

import styles from '../text-cell.module.scss';

interface TextMenuProps {
  editor: Editor;
  showLinkMenu: () => void;
  showVideoMenu: () => void;
}

export const TextMenu = ({ editor, showLinkMenu, showVideoMenu }: TextMenuProps) => {
  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor.isActive('bold'),
      isItalic: editor.isActive('italic'),
      isUnderline: editor.isActive('underline'),
      isBlockquote: editor.isActive('blockquote'),
      isCode: editor.isActive('code'),
      isCodeBlock: editor.isActive('codeBlock'),
      isHeading1: editor.isActive('heading', { level: 1 }),
      isHeading2: editor.isActive('heading', { level: 2 }),
      isHeading3: editor.isActive('heading', { level: 3 }),
    }),
  });

  const handleBlockTypeChange = (blockType: BlockType) => {
    switch (blockType) {
      case 'paragraph':
        editor.chain().focus().setParagraph().run();
        break;
      case 'heading1':
        editor.chain().focus().toggleHeading({ level: 1 }).run();
        break;
      case 'heading2':
        editor.chain().focus().toggleHeading({ level: 2 }).run();
        break;
      case 'heading3':
        editor.chain().focus().toggleHeading({ level: 3 }).run();
        break;
    }
  };

  return (
    <div className={styles.popupMenu} onMouseDown={(event) => event.preventDefault()}>
      <BlockTypeDropdown state={editorState} onChange={handleBlockTypeChange} />
      <div className={styles.separator} />
      <Tooltip
        content={
          <>
            Bold <ShortcutIndicator shortcut={['cmd', 'B']} size="small" />
          </>
        }>
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={classNames({ [styles.active]: editorState.isBold })}>
          <Icon name="Bold" size={16} />
        </button>
      </Tooltip>
      <Tooltip
        content={
          <>
            Italic <ShortcutIndicator shortcut={['cmd', 'I']} size="small" />
          </>
        }>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={classNames({ [styles.active]: editorState.isItalic })}>
          <Icon name="Italic" size={16} />
        </button>
      </Tooltip>
      <Tooltip
        content={
          <>
            Underline <ShortcutIndicator shortcut={['cmd', 'U']} size="small" />
          </>
        }>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={classNames({ [styles.active]: editorState.isUnderline })}>
          <Icon name="Underline" size={16} />
        </button>
      </Tooltip>
      <Tooltip
        content={
          <>
            Link <ShortcutIndicator shortcut={['cmd', 'K']} size="small" />
          </>
        }>
        <button onClick={() => showLinkMenu()}>
          <Icon name="Link" size={16} />
        </button>
      </Tooltip>
      <Tooltip
        content={
          <>
            Quote <ShortcutIndicator shortcut={['cmd', 'shift', 'B']} size="small" />
          </>
        }>
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={classNames({ [styles.active]: editorState.isBlockquote })}>
          <Icon name="Quote" size={16} />
        </button>
      </Tooltip>
      <Tooltip
        content={
          <>
            Inline code snippet <ShortcutIndicator shortcut={['cmd', 'E']} size="small" />
          </>
        }>
        <button
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={classNames({ [styles.active]: editorState.isCode })}>
          <Icon name="CodeInline" size={16} />
        </button>
      </Tooltip>
      <Tooltip
        content={
          <>
            Code block <ShortcutIndicator shortcut={['cmd', 'alt', 'C']} size="small" />
          </>
        }>
        <button
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={classNames({ [styles.active]: editorState.isCodeBlock })}>
          <Icon name="Code" size={16} />
        </button>
      </Tooltip>
      <Tooltip content="Embed video">
        <button onClick={() => showVideoMenu()}>
          <Icon name="Video" size={16} />
        </button>
      </Tooltip>
    </div>
  );
};
